/**
 * Browser environment handlers (env:*).
 *
 * These handlers are intentionally defensive so flows can also execute in
 * SSR, tests, and non-browser runtimes where browser globals are unavailable.
 */

import type { FlowStep } from "../types/flow";
import type { ExecutionContext } from "../context/execution-context";
import { parsePath } from "../expressions/path";
import { registerStepHandler } from "../runtime/step-registry";

function asRecord(step: FlowStep): Record<string, unknown> {
 return step as unknown as Record<string, unknown>;
}

function readBrowserEnvironment(): Record<string, string> {
 const environment: Record<string, string> = {};

 if (typeof navigator !== "undefined") {
 const language = typeof navigator.language === "string" ? navigator.language.trim() : "";
 if (language) environment.language = language;
 }

 try {
 const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
 if (typeof timezone === "string" && timezone.trim()) {
 environment.timezone = timezone.trim();
 }
 } catch {
 // Intl may be unavailable or incomplete in a constrained runtime.
 }

 if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
 try {
 environment.theme = window.matchMedia("(prefers-color-scheme: dark)").matches
 ? "dark"
 : "light";
 } catch {
 // Ignore unsupported media-query implementations.
 }
 }

 return environment;
}

registerStepHandler("env:capture", (step, ctx: ExecutionContext) => {
 const record = asRecord(step);
 const output = typeof record.output === "string"
 ? record.output.trim()
 : typeof record.out === "string"
 ? record.out.trim()
 : "@state.environment";

 const target = parsePath(output);
 if (!target) return;

 const detected = readBrowserEnvironment();
 if (Object.keys(detected).length >0) {
 ctx.writeState(target.scope, target.segments, detected);
 }
});